'use client'
import Section from 'components/Section'
import { LinkButton, MediaImageStandard, Typography } from 'components/Contentful'
import { useEffect, useState } from 'react'
import { ChevronDown } from 'react-feather'
import { Accordion, AccordionDetails, AccordionSummary, Grid, Tab, Tabs } from '@achieve/ascend'
import { AchieveLink } from 'components/AchieveLink'
import { Carousel } from 'components/Carousels'
import { Button } from 'components/Button'
import { BLOCKS } from '@contentful/rich-text-types'
import { useMemoizedContentGetter } from 'utils/contentful'
import { SITE_ENVIRONMENTS } from 'constants/websites'
import {} from 'react'
import classNames from 'classnames'

const ItemComponentCarousel = ({ item, index }) => {
  const config = item?.config || {}
  const bgCarouselColor = item?.config?.bgCarouselColor || 'bg-white'
  return (
    <div className={`flex flex-col justify-between p-4 ${bgCarouselColor}`}>
      <div className="mt-2 flex items-center justify-center">
        <div>
          <MediaImageStandard
            content={item?.image}
            layout="fixed"
            className="h-[250px] w-[271px]]"
            height={250}
            width={271}
            alt={item?.image?.title}
            unoptimized={true}
            fetchPriority="high"
            preload={index === 0}
            quality={100}
            objectFit="contain"
          />
        </div>
      </div>
      <div>
        {item?.title && (
          <Typography
            content={item?.title}
            variant="bodyLg"
            fontWeight="medium"
            className="pt-5 pb-3"
          />
        )}
        {item?.subTitle && (
          <div className="leading-[140%] text-sm [&>ul]:m-0 [&>p>span>a]:text-black [&>p>span>a:hover]:text-blue-500">
            <Typography content={item?.subTitle} variant="bodySm" fontWeight="regular" />
          </div>
        )}

        {config?.ctaHref && (
          <div className="flex justify-start pt-5">
            <Button
              track={{
                ...config?.ctaEvent,
              }}
              variant="contained"
              color="primary"
              className="shadow-md w-full lg:w-auto"
            >
              <a href={config?.ctaHref} target="_blank" rel="noreferrer" className="no-underline">
                <Typography
                  content={config?.ctaText}
                  className="text-white"
                  variantOverride={{ [BLOCKS.PARAGRAPH]: 'bodyLg' }}
                  fontWeight="bold"
                ></Typography>
              </a>
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

function PromiseSliderModule({
  grid,
  eyebrow,
  title,
  subtitle,
  linkButton,
  sectionVariationObject,
  config,
}) {
  const items = grid?.gridItemsCollection?.items || []
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'
  const rowReverse = config?.jsonContent?.rowReverse || false
  const bgColor = config?.jsonContent?.bgColor || 'bgDefault'
  const bgCarouselColor = config?.jsonContent?.bgCarouselColor || 'bg-white'
  const containerClassName = config?.jsonContent?.containerClassName || ''
  const { withIndicators = true } = config?.jsonContent || {}

  const stickyTabsContent =
    items?.map((item) => ({
      tabLabel: item?.title?.json?.content?.[0]?.content[0]?.value,
      tabItem: null,
    })) || []
  const [currentSlider, setCurrentSlider] = useState(null)
  useEffect(() => {
    setTimeout(() => {
      setCurrentSlider(0)
    }, 100)
  }, [])
  return (
    <Section
      className={containerClassName}
      contain={true}
      variant={bgColor}
      data-testid="promise-slider-section"
    >
      <div className="w-full">
        {eyebrow && (
          <Typography
            content={eyebrow?.textContent}
            variant="labelSm"
            color="gray"
            fontWeight="bold"
            className="uppercase pb-5 pt-12 text-center tracking-eyebrow"
          />
        )}
        {title && (
          <Typography
            content={title?.textContent}
            variant="headingLg"
            mobileVariant="headingMd"
            responsive={true}
            className={`pb-6 text-center ${!subtitle ? 'md:pb-12' : ''}`}
            data-testid="promise-slider-title"
          />
        )}
        {subtitle && (
          <Typography
            content={subtitle?.textContent}
            variant="displayXs"
            fontWeight="regular"
            className={`pb-6 text-center ${!linkButton ? 'md:pb-12' : ''}`}
            data-testid="promise-slider-subtitle"
          />
        )}
        {linkButton && (
          <div className={classNames('flex flex-col justify-center items-center w-full mb-8')}>
            <LinkButton
              className={classNames('min-w-[293px] h-[47px]', {
                'bg-[#cb000e]': siteEnv === SITE_ENVIRONMENTS.FDR,
              })}
              content={linkButton}
              typographicOptions={{
                variantOverride: { [BLOCKS.PARAGRAPH]: 'bodyLg' },
                fontWeight: 'bold',
                'data-testid': `promise-slider-button-text`,
                className: 'min-w-[105px] text-center',
              }}
              color={'primary'}
              data-testid={`promise-slider-button`}
              track={{
                ...{
                  list_name: 'Personalized Promise Slider CTA',
                  click_id: linkButton.linkText?.json.content?.[0]?.content[0]?.value,
                  click_text: linkButton.linkText?.json.content[0]?.content[0]?.value,
                  click_url: linkButton.linkHref,
                  nav_link_section: 'Promise Slider',
                  click_position: '0',
                  track_event: 'internal_campaign_click',
                  event_action: 'button_click',
                  event_type: 'button_click',
                },
                ...linkButton.event,
                sectionVariationObject,
              }}
            />
          </div>
        )}
      </div>
      {items.length && (
        <>
          {/* Desktop View, after testing we can test isMobile hook to remove unnecessary HTML */}
          <div
            className={classNames('w-full hidden md:flex pb-6', {
              'flex-row-reverse': rowReverse,
              'flex-row': !rowReverse,
            })}
            data-testid="promise-slider-container"
          >
            <div className="w-8/12 sm:w-7/12">
              <div
                className="mt-2 flex items-center justify-center"
                data-testid="promise-slider-image-container"
              >
                <div>
                  <MediaImageStandard
                    content={items?.[currentSlider]?.image}
                    layout="fixed"
                    className="h-[400px] w-[433px]]"
                    height={400}
                    width={433}
                    alt={items?.[currentSlider]?.image?.title}
                    unoptimized={true}
                    fetchPriority="high"
                    preload={true}
                    quality={100}
                    objectFit="contain"
                  />
                </div>
              </div>
            </div>

            <div className="w-4/12 sm:w-5/12" data-testid="promise-slider-content-container">
              {items.map((promise, index) => {
                return (
                  <Accordion
                    key={promise?.sys?.id}
                    expanded={currentSlider === index}
                    onChange={() => setCurrentSlider(index)}
                    className={`${bgCarouselColor}`}
                    data-testid={`promise-slider-accordion-item-${index}`}
                  >
                    <AccordionSummary expandIcon={<ChevronDown />}>
                      {promise?.title && (
                        <Typography
                          content={promise?.title}
                          variant="headingMd"
                          fontWeight="medium"
                          color="black"
                          data-testid="promise-slider-accordion-item-title"
                        />
                      )}
                    </AccordionSummary>
                    {promise?.subTitle && (
                      <AccordionDetails>
                        <div
                          className="[&>ul]:m-0 [&>p>span>a]:text-black [&>p>span>a:hover]:text-blue-500"
                          data-testid="promise-slider-accordion-item-list-content"
                        >
                          <Typography
                            content={promise?.subTitle}
                            variant="bodyMd"
                            fontWeight="regular"
                          />
                        </div>
                        {promise?.config?.ctaHref && (
                          <div
                            className="flex justify-start p-4"
                            data-testid="promise-slider-accordion-item-button-container"
                          >
                            <AchieveLink
                              href={promise?.config?.ctaHref}
                              track={{
                                ...promise?.config?.ctaEvent,
                                sectionVariationObject,
                              }}
                              underline="hover"
                            >
                              <Typography
                                variant="bodyMd"
                                fontWeight="bold"
                                content={promise?.config?.ctaText}
                              />
                            </AchieveLink>
                          </div>
                        )}
                      </AccordionDetails>
                    )}
                  </Accordion>
                )
              })}
            </div>
          </div>

          {/* Mobile View, after testing we can test isMobile hook to remove unnecessary HTML */}

          <div className="md:hidden">
            <div className="relative max-w-full" data-testid="sticky-tabs-outer-container">
              <Grid
                container
                className={`sticky top-0 z-9 block justify-center bg-transparent -translate-y-px data-[stuck=true]:${bgCarouselColor}`}
                data-testid="sticky-tabs-container"
              >
                <div>
                  <Tabs
                    value={stickyTabsContent[currentSlider]?.tabLabel ?? false}
                    onChange={(e, newValue) => {
                      setCurrentSlider(
                        stickyTabsContent?.findIndex((item) => item?.tabLabel === newValue)
                      )
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    className="border-b"
                    allowScrollButtonsMobile={true}
                    aria-label="sticky scrollable tabs component"
                  >
                    {stickyTabsContent.map(({ tabLabel }, i) => (
                      <Tab
                        key={`tab-${i}`}
                        label={tabLabel}
                        value={tabLabel}
                        data-testid={`tab-${i}`}
                      />
                    ))}
                  </Tabs>
                </div>
              </Grid>
            </div>

            <Carousel
              autoPlay={false}
              carouselItems={items}
              itemComponent={ItemComponentCarousel}
              variant="preview-full"
              withIndicators={withIndicators}
              disableArrows={true}
              classNameIndicatorDots="flex bg-[#3300ff] mt-8 mb-8 lg:hidden"
              idPrefix="promise-slider"
              setValue={currentSlider}
              className="block p-0 [&>div]:items-center"
              cbOnChange={(pos) => setCurrentSlider(pos)}
            />
          </div>
        </>
      )}
    </Section>
  )
}

function PromiseSlider({ content }) {
  const sectionVariationObject = {
    ...content?.fields?.experiment,
    variant: content?.fields?.experimentVariation,
  }
  const { grid, eyebrow, title, subtitle, linkButton, config } = useMemoizedContentGetter(content, [
    'grid',
    'eyebrow',
    'title',
    'subtitle',
    'linkButton',
    'config',
  ])
  return (
    <PromiseSliderModule
      grid={grid}
      eyebrow={eyebrow}
      title={title}
      subtitle={subtitle}
      sectionVariationObject={sectionVariationObject}
      linkButton={linkButton}
      config={config}
    />
  )
}

export { PromiseSlider, PromiseSliderModule }
export default PromiseSlider
